<template>
  <div class="campaign-period-container">
    <strong class="text-cinza-d10" style="font-size: 16px">Período de validade</strong>
    <div class="offer-calendar-container">
      <div class="mt-2 validity-period">
        <div class="input-container" :class="{ 'no-hover': disabled }">
          <strong class="text-cinza-l20">Data inicial</strong>
          <v-skeleton-loader v-if="loadingAction" height="40" width="221" type="button" />
          <date-picker
            v-else
            :date.sync="initialDate"
            :min="minInitialDate"
            :max="maxInitialDate"
            :error="initialDateError.length > 0"
            :error-messages="initialDateError"
            :disabled="disabled"
            outlined
            dense
          />
        </div>

        <div class="input-container" :class="{ 'no-hover': disabled || campaignHasOffers }">
          <strong class="text-cinza-l20">Data final</strong>
          <v-skeleton-loader v-if="loadingAction" height="40" width="221" type="button" />
          <date-picker
            v-else
            :date.sync="finalDate"
            :min="minFinalDate"
            :error="finalDateError.length > 0"
            :error-messages="finalDateError"
            :disabled="disabled || campaignHasOffers"
            outlined
            dense
          />
        </div>
      </div>

      <v-skeleton-loader v-if="loadingAction" class="align-self-center mt-4" height="40" width="158" type="card-heading" />
      <div v-else-if="numberOfActive || numberOfInactive" class="legend-container mt-4">
        <div>
          <div v-if="numberOfActive" class="legend-item">
            <div class="legend-icon marca-1" />
            <span>{{ numberOfActive }}</span>
            <span class="legend-text">dias com oferta</span>
          </div>

          <div v-if="numberOfInactive && campaignHasOffers" class="legend-item">
            <div class="legend-icon vermelho-0" />
            <span>{{ numberOfInactive }}</span>
            <span class="legend-text">dias sem oferta</span>
          </div>
        </div>
      </div>

      <v-skeleton-loader v-if="campaignHasOffers && loadingAction" class="align-self-end" height="40" width="212" type="button" />
      <div v-else-if="campaignHasOffers" class="mt-4">
        <mf-offer-calendar :days="offerCalendarDays" show-only-current-month />
      </div>
    </div>

    <div class="info-card-container" :class="{ 'mt-4': loadingAction }">
      <mf-info-card v-if="campaignHasOffers && numberOfInactive > 0" :vHtml="infoCardContent" />
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { mapActions, mapGetters } from 'vuex'
import DatePicker from '@/components/DatePicker.vue'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'

export default {
  name: 'CampaignPeriod',
  components: {
    DatePicker,
  },
  mixins: [mariaCampaign],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    initialDateError: '',
    finalDateError: '',
  }),
  computed: {
    ...mapGetters(['daysOnOffers', 'campaignDays']),
    initialDate: {
      get() {
        return this.campaign.initial_date
      },
      set(value) {
        if (value > this.finalDate) this.setOmniCampaignKey({ key: 'final_date', payload: '' })
        this.setOmniCampaignKey({ key: 'initial_date', payload: value })
      },
    },
    finalDate: {
      get() {
        return this.campaign.final_date
      },
      set(value) {
        this.setOmniCampaignKey({ key: 'final_date', payload: value })
      },
    },
    minInitialDate() {
      if (this.campaignHasOffers) {
        const startDate = this.findEarliestStartDate().minus({ days: 30 })
        return startDate < DateTime.now() ? DateTime.now().toFormat('yyyy-MM-dd') : startDate.toFormat('yyyy-MM-dd')
      }
      return DateTime.now().toFormat('yyyy-MM-dd')
    },
    maxInitialDate() {
      if (this.campaignHasOffers) {
        return this.findLatestEndDate().minus({ days: 1 }).toFormat('yyyy-MM-dd')
      }
      return ''
    },
    minFinalDate() {
      if (this.initialDate) return this.initialDate
      return this.minInitialDate
    },
    hasNoDateDefined() {
      return !this.initialDate || !this.finalDate || DateTime.fromISO(this.initialDate).invalid || DateTime.fromISO(this.finalDate).invalid
    },
    numberOfActive() {
      return this.offerCalendarDays.filter((day) => day.active).length
    },
    numberOfInactive() {
      return this.offerCalendarDays.filter((day) => !day.active).length
    },
    offerCalendarDays() {
      let days = []
      if (this.hasNoDateDefined) return days
      let startDate = DateTime.fromISO(this.initialDate)
      const endDate = DateTime.fromISO(this.finalDate)
      while (startDate <= endDate) {
        days.push(this.createDayObject(startDate))
        startDate = startDate.plus({ days: 1 })
      }
      return days
    },
    infoCardContent() {
      return `<span style="color: #004d99">Disparos contendo produtos ofertados (variáveis de <strong>'melhor produto'</strong>), não são enviados em dias sem ofertas.</span>`
    },
  },
  watch: {
    initialDate() {
      this.initialDateError = ''
    },
    finalDate() {
      this.finalDateError = ''
    },
  },
  async mounted() {
    this.setLoadingAction(true)
    try {
      if (this.campaignHasOffers) {
        const { offerScheduleIds, franchiseOfferScheduleIds } = this.getOfferSchedulesIds()
        await this.getDaysOnOffers({
          offers_schedule_ids: offerScheduleIds,
          franchise_offers_schedule_ids: franchiseOfferScheduleIds,
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      this.setLoadingAction(false)
    }
  },
  methods: {
    ...mapActions(['getDaysOnOffers']),
    getOfferSchedulesIds() {
      let offerScheduleIds = []
      let franchiseOfferScheduleIds = []
      this.campaign.offer_schedules.forEach((offer) => {
        offer.from_franchise
          ? franchiseOfferScheduleIds.push(offer.offer_schedule_id || offer._id || offer?.offer_schedule?._id)
          : offerScheduleIds.push(offer.offer_schedule_id || offer._id || offer?.offer_schedule?._id)
      })
      return { offerScheduleIds, franchiseOfferScheduleIds }
    },
    setDateValue() {
      this.setOmniCampaignKey({ key: 'initial_date', payload: this.findEarliestStartDate().toFormat('yyyy-MM-dd') })
      const latestEndDate = this.findLatestEndDate()
      this.setOmniCampaignKey({ key: 'final_date', payload: latestEndDate ? latestEndDate.toFormat('yyyy-MM-dd') : '' })
    },
    findEarliestStartDate() {
      let startDate = DateTime.min(...this.campaign.offer_schedules.map((offer) => DateTime.fromISO(offer.start_date))) || DateTime.now()
      if (this.campaignHasOffers) {
        return startDate < DateTime.now() ? DateTime.now() : startDate
      }
      return startDate
    },
    findLatestEndDate() {
      if (this.campaignHasOffers) {
        return DateTime.max(...this.campaign.offer_schedules.map((offer) => DateTime.fromISO(offer.end_date)))
      } else if (this.finalDate) {
        return DateTime.fromISO(this.finalDate)
      }
      return ''
    },
    createDayObject(date) {
      const dayString = date.toISODate()
      const active = this.daysOnOffers.days.find((d) => DateTime.fromISO(d.day).toISODate() === dayString)?.active || false
      return {
        date: dayString,
        active,
      }
    },
    validate() {
      this.initialDateError = ''
      this.finalDateError = ''
      let isValid = true

      const formattedInitialDate = DateTime.fromISO(this.initialDate)
      const formattedFinalDate = DateTime.fromISO(this.finalDate)

      if (!formattedInitialDate || formattedInitialDate.invalid) {
        this.initialDateError = 'É necessário definir uma data de início.'
        isValid = false
      }

      if (!formattedFinalDate || formattedFinalDate.invalid) {
        this.finalDateError = 'É necessário definir uma data de fim.'
        isValid = false
      }

      if (formattedInitialDate > formattedFinalDate) {
        this.initialDateError = 'A data inicial precisa ser menor que a data final.'
        isValid = false
      }

      if (!this.isMariaCampaignActive && formattedInitialDate.startOf('day') < DateTime.now().startOf('day')) {
        this.initialDateError = 'A data de início deve ser a partir do dia atual.'
        isValid = false
      }

      if (!this.isMariaCampaignActive && formattedInitialDate < DateTime.fromISO(this.minInitialDate)) {
        if (this.campaignHasOffers) {
          this.initialDateError = 'A campanha precisa começar no máximo 30 dias antes do início da primeira oferta selecionada.'
        }
        isValid = false
      }

      if (formattedFinalDate < formattedInitialDate) {
        this.finalDateError = 'A data final não pode ser menor que a inicial.'
        isValid = false
      }

      return isValid
    },
  },
}
</script>

<style lang="scss" scoped>
.campaign-period-container {
  .text-cinza-d10 {
    color: $cinza-d10;
  }

  .info-card-container {
    max-width: 735px;
  }

  .offer-calendar-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;

    .validity-period {
      display: flex;
      align-items: flex-start;
      gap: 20px;

      .input-container {
        max-width: 240px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
    }

    .legend-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 158px;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.4000000059604645px;

        .legend-icon {
          width: 12px;
          height: 12px;
          border-radius: 4px;
        }

        .legend-text {
          color: $cinza-l10;
        }
      }
    }

    .text-cinza-l20 {
      color: $cinza-l20;
    }

    ::v-deep .v-skeleton-loader {
      .v-skeleton-loader__button,
      .v-skeleton-loader__heading {
        width: auto !important;
      }
    }
  }
  .no-hover {
    pointer-events: none;
    cursor: default;

    * {
      cursor: default !important;
    }
  }
}
</style>
